import { IEventDetail, IPlugin } from "@easepick/base-plugin";
import { DateTime, LockPlugin } from "@easepick/bundle";
import { ILockPartlyConfig, TLockPartlyRange } from "./interface";
import { addTooltipDayOverlay } from "../Utils/Tooltip";

export class LockPartlyPlugin extends LockPlugin implements IPlugin {
    private static TOOLTIP_REGEX = new RegExp("\\{\\s*time\\s*}");

    declare binds: {
        onView: () => void;
        extendedOnView: () => void;
        setRanges: (ranges: TLockPartlyRange[]) => void;
    };

    public options: ILockPartlyConfig = {
        lagTimeHours: 0,
        ranges: [],
        open: null,
        minDate: null,
        maxDate: null,
        minDays: null,
        maxDays: null,
        selectForward: null,
        selectBackward: null,
        presets: true,
        inseparable: false,
        filter: null,
        preventOverlap: false,
        locale: {
            tooltip: {
                start: null,
                end: null,
                rangeAfterBlackout: null
            }
        },
        blackouts: []
    };

    constructor() {
        super();
        this.binds.extendedOnView = this.handleView.bind(this);
        this.binds.setRanges = this.handleRanges.bind(this);
    }

    getName(): string {
        return "LockPartlyPlugin";
    }

    onAttach(): void {
        super.onAttach();
        this.sortBlackouts();
        this.picker.on("view", this.binds.extendedOnView);
        Object.defineProperties(this.picker, {
            setRanges: {
                configurable: true,
                value: this.binds.setRanges
            }
        });
    }

    onDetach(): void {
        super.onDetach();
        this.picker.off("view", this.binds.extendedOnView);
    }

    sortBlackouts() {
        this.options.blackouts.sort((a, b) => {
            if (a.isBefore(b)) return -1;
            if (a.isAfter(b)) return 1;
            return 0;
        });
    }

    /**
     * handle view event
     * @param event
     */
    handleView(event: CustomEvent) {
        const { view, target, date }: IEventDetail = event.detail;
        if (view === "CalendarDay") {
            let lockingClass, tooltip;
            if (this.isEndOfRange(date) && this.isStartOfRange(date)) {
                target.classList.add("locked");
            } else {
                if (this.isStartOfRange(date) && this.isPartlyLockedEndDate(date) && this.isEndDateCandidate(date)) {
                    lockingClass = "locked-end";
                    tooltip = this.options.locale.tooltip.start;
                }
                if (this.isEndOfRange(date) && this.isPartlyLockedStartDate(date) && this.isEndDateCandidate(date)) {
                    lockingClass = "locked-start";
                    tooltip = this.options.locale.tooltip.end;
                }
                if (this.isPartlyLockedByRangeAfterBlackout(date)) {
                    if (this.picker.datePicked.length && this.isAvailable(date)) {
                        lockingClass = "locked-end";
                    } else {
                        target.classList.add("locked");
                        tooltip = this.options.locale.tooltip.rangeAfterBlackout;
                    }
                }
            }
            if (lockingClass) {
                target.classList.add("partly-locked", lockingClass);
                target.classList.remove("locked");
            }
            if (tooltip) {
                this.addToolTip(view, target, date, tooltip);
            }
            target.classList.toggle("not-available", !this.isAvailable(date));
        }
    }

    handleRanges(ranges: TLockPartlyRange[]) {
        this.options.ranges = ranges;
    }

    /**
     * check if date is available for picking as end date
     * @param date
     * @private
     */
    private isAvailable(date: DateTime): boolean {
        if (this.options.inseparable && this.picker.datePicked[0]) {
            const rangeBefore = this.getLastRangeBefore(this.picker.datePicked[0]);
            const rangeAfter = this.getFirstRangeAfter(this.picker.datePicked[0]);
            if (rangeBefore && date.isSameOrBefore(rangeBefore.start)) {
                return false;
            }
            if (rangeAfter && date.isSameOrAfter(rangeAfter.end)) {
                return false;
            }
        }
        return true;
    }

    /**
     * get last range before the given date
     * this includes a range where the end date is the same as the given date
     * @param date
     * @private
     */
    private getLastRangeBefore(date: DateTime): TLockPartlyRange {
        return this.options.ranges
            .filter((r) => r.end.isSameOrBefore(date))
            .sort((a, b) => {
                if (a.start > b.start) return 1;
                if (a.start < b.start) return -1;
                return 0;
            })
            .pop();
    }

    /**
     * get first range after the given date
     * this includes a range where the start date is the same as the given date
     * @param date
     * @private
     */
    private getFirstRangeAfter(date: DateTime): TLockPartlyRange {
        return this.options.ranges
            .filter((r) => r.start.isSameOrAfter(date))
            .sort((a, b) => {
                if (a.start > b.start) return 1;
                if (a.start < b.start) return -1;
                return 0;
            })
            .shift();
    }

    /**
     * check if the given date is the start date of one of the ranges
     * @param date
     * @private
     */
    private isStartOfRange(date: DateTime): boolean {
        return !!this.options.ranges.find((r) => r.start.isSame(date));
    }

    /**
     * check if the given date is the end date of one of the ranges
     * @param date
     * @private
     */
    private isEndOfRange(date: DateTime): boolean {
        return !!this.options.ranges.find((r) => r.end.isSame(date));
    }

    /**
     * check if date is blocked as start date because next possible end date is blocked
     * @param date
     * @private
     */
    private isPartlyLockedByRangeAfterBlackout(date: DateTime): boolean {
        const dayAfter = date.clone().add(1);
        if (!this.options.blackouts.find((r) => r.isSame(dayAfter))) return false;
        while (this.options.blackouts.find((b) => b.isSame(dayAfter))) {
            dayAfter.add(1);
        }
        if (this.isStartOfRange(dayAfter)) {
            const day = dayAfter.getDay();
            const range = this.options.ranges.find((r) => r.start.isSame(dayAfter));
            const start = range.start.getHours() - this.options.lagTimeHours;
            switch (day) {
                case 0: // sunday
                    return start < this.options.open.sunday.start;
                case 6: // saturday
                    return start < this.options.open.saturday.start;
                default:
                    return start < this.options.open.weekdays.start;
            }
        }
        return false;
    }

    /**
     * obtain range by start date
     * @param date
     * @private
     */
    private getRangeByStartDate(date: DateTime): TLockPartlyRange {
        return this.options.ranges.find((r) => r.start.isSame(date));
    }

    /**
     * obtain range by end date
     * @param date
     * @private
     */
    private getRangeByEndDate(date: DateTime): TLockPartlyRange {
        return this.options.ranges.find((r) => r.end.isSame(date));
    }

    /**
     * check if the given date is partly locked as end date
     * (cannot be chosen as end date)
     * @param date
     * @private
     */
    private isPartlyLockedEndDate(date: DateTime): boolean {
        const day = date.getDay();
        const range = this.options.ranges.find((r) => r.start.isSame(date));
        const start = range.start.getHours() - this.options.lagTimeHours;
        switch (day) {
            case 0: // sunday
                return start >= this.options.open.sunday.start;
            case 6: // saturday
                return start >= this.options.open.saturday.start;
            default:
                return start >= this.options.open.weekdays.start;
        }
    }

    /**
     * check if the given date is partly locked as start date
     * (cannot be chosen as start date)
     * @param date
     * @private
     */
    private isPartlyLockedStartDate(date: DateTime): boolean {
        const day = date.getDay();
        const range = this.options.ranges.find((r) => r.end.isSame(date));
        const end = range.end.getHours() + this.options.lagTimeHours;
        switch (day) {
            case 0: // sunday
                return end <= this.options.open.sunday.end;
            case 6: // saturday
                return end <= this.options.open.saturday.end;
            default:
                return end <= this.options.open.weekdays.end;
        }
    }

    /**
     * if picked start date is the end date of a range
     * check if the start date of the current date can be an end date
     * @param date
     * @private
     */
    private isEndDateCandidate(date: DateTime): boolean {
        if (this.options.preventOverlap && this.picker.datePicked.length) {
            const pickedStart = [...this.picker.datePicked].shift();
            const pickedEnd = [...this.picker.datePicked].pop();
            const day = date.getDay();

            const rangeAfter = this.getFirstRangeAfter(pickedStart);

            // picked start date could be the last day of an existing range
            if (rangeAfter && date.isSame(rangeAfter.start) && this.isEndOfRange(pickedStart)) {
                // we have to check if the time of the end date of the existing range is smaller than
                const range = this.getRangeByEndDate(pickedStart);
                const nextRange = this.getFirstRangeAfter(range.end);
                const latestEnd = nextRange.start.getHours() - this.options.lagTimeHours;
                switch (day) {
                    case 0: // sunday
                        return range.end.getHours() <= Math.min(latestEnd, this.options.open.sunday.end);
                    case 6: // saturday
                        return range.end.getHours() <= Math.min(latestEnd, this.options.open.saturday.end);
                    default:
                        return range.end.getHours() <= Math.min(latestEnd, this.options.open.weekdays.end);
                }
            }

            const rangeBefore = this.getLastRangeBefore(pickedStart);

            if (rangeBefore && date.isSame(rangeBefore.end) && this.isStartOfRange(pickedEnd)) {
                const range = this.getRangeByStartDate(pickedEnd);
                const rangeBefore = this.getLastRangeBefore(range.start);
                if (rangeBefore) {
                    const earliestStart = rangeBefore.end.getHours() + this.options.lagTimeHours;
                    switch (day) {
                        case 0: // sunday
                            return range.start.getHours() >= Math.max(earliestStart, this.options.open.sunday.start);
                        case 6: // saturday
                            return range.start.getHours() >= Math.max(earliestStart, this.options.open.saturday.start);
                        default:
                            return range.start.getHours() >= Math.max(earliestStart, this.options.open.weekdays.start);
                    }
                }
            }
        }
        return true;
    }

    /**
     * obtain start or end hour of given date
     * @param date
     * @private
     */
    private getHourOfDateByRange(date: DateTime): number {
        const isStart = this.isStartOfRange(date);
        const ending = isStart ? "start" : "end";
        const range = isStart ? this.getRangeByStartDate(date) : this.getRangeByEndDate(date);
        return (
            (range[ending] as DateTime).getHours() +
            (isStart ? this.options.lagTimeHours * -1 : this.options.lagTimeHours)
        );
    }

    /**
     * add a tooltip if set
     * @param view
     * @param target
     * @param date
     * @param text
     * @private
     */
    private addToolTip(view: string, target: HTMLElement, date: DateTime, text: string): void {
        if (view !== "CalendarDay") return;
        const grid = target.parentElement;
        if (grid) {
            if (text.match(LockPartlyPlugin.TOOLTIP_REGEX)) {
                const hour = this.getHourOfDateByRange(date);
                text = text.replace(LockPartlyPlugin.TOOLTIP_REGEX, hour.toString());
            }
            if (target.classList.contains("locked")) {
                addTooltipDayOverlay(target, text, grid);
            } else {
                target.title = text;
            }
        }
    }
}
